import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, FormikProps } from "formik";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRectangleHistoryCircleUser } from "@fortawesome/pro-regular-svg-icons";
import { ExclamationIcon } from "../../assets/iconComponents/ExclamationIcon";
import { Button } from "../shared/Button/Button";
import { themePalette } from "../../theme";
import { LANDING_PAGE_URL } from "../../utils/urlConstants";

import {
  EditReport,
  ErrorText,
  ErrorWrapper,
  GeneralInfoCard,
  GeneralInfoColumn,
  GeneralInfoColumns,
  GeneralInfoHeader,
  GeneralInfoText,
} from "./UserReport.styles";
import FormField from "../shared/TextField/FormField";
import { DetailedRequisitionSerializer } from "../../recoil/types";
import {
  PatchSpecificRequisition,
  updateSpecificRequsitionInitialValues,
} from "./Services/UserReportService";
import {
  convertDetailedRequisitionToReportResults,
  searchCharacterCountValid,
} from "../../recoil/helpers";
import ResultsInfo from "./ResultsInfo";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import {
  defaultRequisitions,
  missingRequisitions,
  searchRequisitions,
} from "../../recoil/atoms";
import {
  RefreshMissingInfo,
  RefreshRequisitionRows,
} from "../Grid/Services/GridService";
import { useRequisitionSetter } from "../../recoil/useRequisitionSetter";
import { formatWithTimezoneOffset } from "../../utils/helpers";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import CfcReportDownload from "./CfcReportDownload";
import { disaffiliateRequisition } from "../GlobalSearch/Services/GlobalSearchService";
import { AffiliationConfirmationContent } from "../GlobalSearch/AffiliationConfirmationContent";
import { GlobalSearchTooltip } from "../GlobalSearch/GlobalSearchTooltip";
import { SESSION_KEY_STATE } from "../GlobalSearch/GlobalSearchModal";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import {
  formatTestOrderedForDisplay,
  getTestOrderTypeFlags,
} from "../../utils/testOrderDisplay";

const GeneralInfoFormField = (
  props: React.ComponentProps<typeof FormField>,
) => <FormField {...props} cardDisplay />;

const GeneralInfo: React.FC<{
  fullReport: DetailedRequisitionSerializer;
  setFullReport: React.Dispatch<
    React.SetStateAction<DetailedRequisitionSerializer | undefined>
  >;
  canViewProviderCfcResults: boolean;
}> = ({ fullReport, setFullReport, canViewProviderCfcResults }) => {
  const [editMode, setEditMode] = useState(false);
  const history = useHistory();
  const { auth, setLoggedOut } = useAuthenticationHook();
  const [, setDefaultRequisitions] = useRecoilState(defaultRequisitions);
  const [, setMissingReqs] = useRecoilState(missingRequisitions);
  const [, setSearchRequisitions] = useRecoilState(searchRequisitions);
  const { setRequisitions, searchText } = useRequisitionSetter();
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmittingDisaffiliation, setIsSubmittingDisaffiliation] =
    useState(false);
  const [disaffiliationActionsDisabled, setDisaffiliationActionsDisabled] =
    useState(false);
  const [disaffiliationError, setDisaffiliationError] = useState(false);
  const reportResults = convertDetailedRequisitionToReportResults(fullReport);
  const carrierPanelGeneCount = fullReport.carrier_screen_panel_gene_count;
  const orderTypeFlags = getTestOrderTypeFlags(
    reportResults.carrier,
    reportResults.aneuploidy,
    carrierPanelGeneCount,
  );
  const isEditingDueDate =
    editMode && fullReport.is_missing_due_date && !fullReport.due_date;

  const handleConfirmDisaffiliation = async () => {
    if (!auth?.token || !fullReport.sfid) return;
    setIsSubmittingDisaffiliation(true);
    setDisaffiliationError(false);
    const ok = await disaffiliateRequisition(
      auth.token,
      setLoggedOut,
      fullReport.sfid,
    );
    if (!ok) {
      console.error("Failed to disaffiliate global search requisition");
      setIsSubmittingDisaffiliation(false);
      setDisaffiliationError(true);
      return;
    }
    TrackAnalyticsEvent(
      "global_search_disaffiliate",
      auth.token,
      auth.username,
    );
    sessionStorage.removeItem(SESSION_KEY_STATE);
    setIsSubmittingDisaffiliation(false);
    setDisaffiliationActionsDisabled(true);
    const refreshRequests = [
      RefreshRequisitionRows(auth.token, setDefaultRequisitions, setLoggedOut),
    ];
    if (searchCharacterCountValid(searchText)) {
      refreshRequests.push(
        RefreshRequisitionRows(
          auth.token,
          setSearchRequisitions,
          setLoggedOut,
          searchText,
        ),
      );
    }
    await Promise.all(refreshRequests);
    history.push(LANDING_PAGE_URL);
  };

  return (
    <div>
      {!loading ? (
        <Formik<{ due_date?: string }>
          initialValues={{
            due_date: fullReport.due_date,
          }}
          onSubmit={async (values, { resetForm }) => {
            setLoading(true);
            try {
              const response = await PatchSpecificRequisition(
                auth!.token,
                fullReport.sfid!,
                {
                  ...updateSpecificRequsitionInitialValues,
                  due_date: values.due_date || "",
                },
                setLoggedOut,
              );
              setLoading(false);
              if (response?.ok) {
                resetForm();
                const newReport = await response.json();
                setFullReport(newReport);
                await RefreshRequisitionRows(
                  auth!.token,
                  setRequisitions,
                  setLoggedOut,
                  searchText,
                );
                await RefreshMissingInfo(
                  auth!.token,
                  setMissingReqs,
                  setLoggedOut,
                );
              } else {
                // setResponseMessage("There was an error - please try again.");
              }
            } catch (e) {
              setLoading(false);
              // setResponseMessage(networkErrorMessage);
            }
          }}
          validateOnChange={false}
        >
          {({
            values,
            handleSubmit,
            setFieldValue,
            resetForm,
          }: FormikProps<{ due_date?: string }>) => (
            <GeneralInfoCard>
              <GeneralInfoHeader>
                <GeneralInfoText id="req-general-information">
                  GENERAL INFORMATION
                </GeneralInfoText>
                {fullReport.is_handoff_affiliated && (
                  <GlobalSearchTooltip title="Remove patient from my list">
                    <IconButton
                      onClick={() => {
                        setDisaffiliationError(false);
                        setDisaffiliationActionsDisabled(false);
                        setConfirmOpen(true);
                      }}
                      size="small"
                      sx={{
                        padding: 0,
                        "&:hover": { opacity: 0.7 },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faRectangleHistoryCircleUser}
                        color={themePalette.redDark}
                      />
                    </IconButton>
                  </GlobalSearchTooltip>
                )}
              </GeneralInfoHeader>
              <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                fullWidth
                maxWidth={false}
                sx={{ "& .MuiDialog-paper": { maxWidth: "1000px" } }}
                PaperProps={{ sx: { borderRadius: "8px" } }}
              >
                <DialogTitle
                  sx={{
                    m: 0,
                    p: 2,
                    backgroundColor: themePalette.neutral50,
                    fontFamily: "bold",
                    fontSize: "18px",
                    fontWeight: 700,
                    color: themePalette.neutral700,
                  }}
                >
                  Confirm Remove Patient
                  <IconButton
                    onClick={() => setConfirmOpen(false)}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ padding: 0 }}>
                  <AffiliationConfirmationContent
                    mode="disaffiliate"
                    patient={fullReport}
                    isSubmitting={isSubmittingDisaffiliation}
                    actionsDisabled={disaffiliationActionsDisabled}
                    error={disaffiliationError}
                    onConfirm={handleConfirmDisaffiliation}
                    onCancel={() => setConfirmOpen(false)}
                  />
                </DialogContent>
              </Dialog>
              {fullReport.is_missing_due_date && (
                <ErrorWrapper>
                  <ErrorText>
                    <ExclamationIcon />
                    <div style={{ paddingTop: "3px" }}>
                      Some details need your attention. Please provide the
                      required information to complete the order.
                    </div>
                  </ErrorText>
                </ErrorWrapper>
              )}

              <GeneralInfoColumns>
                <GeneralInfoColumn
                  role="group"
                  aria-label="Patient information"
                >
                  <GeneralInfoFormField
                    fieldTitle="Order"
                    fieldValue={fullReport.req_identifier || ""}
                  />
                  <GeneralInfoFormField
                    fieldTitle="Last Name"
                    fieldValue={fullReport.last_name || ""}
                  />
                  <GeneralInfoFormField
                    fieldTitle="First Name"
                    fieldValue={fullReport.first_name || ""}
                  />
                  <GeneralInfoFormField
                    fieldTitle="Date of Birth"
                    fieldValue={formatWithTimezoneOffset(
                      fullReport.date_of_birth,
                    )}
                    fieldType="date"
                  />
                  <GeneralInfoFormField
                    fieldTitle="Ethnicity"
                    fieldValue={fullReport.ethnicity || ""}
                  />
                  <GeneralInfoFormField
                    fieldTitle="Gestation"
                    fieldValue={reportResults.gestation}
                  />
                  <GeneralInfoFormField
                    fieldTitle="Due Date"
                    fieldValue={
                      isEditingDueDate
                        ? values.due_date || ""
                        : formatWithTimezoneOffset(
                            fullReport.due_date || values.due_date,
                          )
                    }
                    onFieldChange={(val) => setFieldValue("due_date", val)}
                    fieldType="date"
                    edit={isEditingDueDate}
                    isError={fullReport.is_missing_due_date}
                  />
                  <GeneralInfoFormField
                    fieldTitle="Egg Donor Status"
                    fieldValue={reportResults.eggDonor}
                  />
                </GeneralInfoColumn>
                <GeneralInfoColumn role="group" aria-label="Order and results">
                  <GeneralInfoFormField
                    fieldTitle="Tests Ordered"
                    fieldValue={formatTestOrderedForDisplay(
                      fullReport.test_ordered,
                      carrierPanelGeneCount,
                      fullReport.fetal_antigen_panel,
                      orderTypeFlags,
                    )}
                  />
                  <GeneralInfoFormField
                    fieldTitle="Ordering Provider"
                    fieldValue={fullReport.physician_name || ""}
                  />
                  <GeneralInfoFormField
                    fieldTitle="Collection Date"
                    fieldValue={formatWithTimezoneOffset(
                      fullReport.collection_date,
                    )}
                  />
                  <GeneralInfoFormField
                    fieldTitle="Received Date"
                    fieldValue={formatWithTimezoneOffset(
                      fullReport.date_received,
                    )}
                  />
                  <GeneralInfoFormField
                    fieldTitle="Report Date"
                    fieldValue={fullReport.latest_report_date || ""}
                  />
                  <ResultsInfo
                    value={reportResults.carrier}
                    data={fullReport}
                    aneuploidy={false}
                    carrier
                    title="Carrier Results"
                  />
                  <ResultsInfo
                    value={reportResults.aneuploidy}
                    data={fullReport}
                    aneuploidy
                    carrier={false}
                    title="Aneuploidy Results"
                  />
                  {canViewProviderCfcResults && (
                    <CfcReportDownload cfcReport={fullReport.cfc_report} />
                  )}
                  <ResultsInfo
                    value={reportResults.RhD}
                    data={fullReport}
                    aneuploidy={false}
                    carrier={false}
                    title="RhD"
                  />
                  <ResultsInfo
                    value={reportResults.fetalSex}
                    data={fullReport}
                    aneuploidy={false}
                    carrier={false}
                    title="Fetal Sex"
                  />
                </GeneralInfoColumn>
              </GeneralInfoColumns>
              {fullReport.is_missing_due_date && (
                <EditReport>
                  {!editMode && (
                    <Button
                      label="EDIT"
                      onClick={() => setEditMode(true)}
                      size="medium"
                    />
                  )}
                  {editMode && (
                    <Box sx={{ display: "flex", gap: "15px" }}>
                      <Button
                        label="CANCEL"
                        onClick={() => {
                          resetForm();
                          setEditMode(false);
                        }}
                        size="medium"
                      />
                      <Button
                        label="SAVE"
                        onClick={() => {
                          handleSubmit();
                          setEditMode(false);
                        }}
                        size="medium"
                        reverse
                        disabled={!values.due_date}
                      />
                    </Box>
                  )}
                </EditReport>
              )}
            </GeneralInfoCard>
          )}
        </Formik>
      ) : (
        <div
          style={{
            height: "100%",
            width: "100%",
            paddingBottom: "100px",
          }}
        >
          <SpinnerLoading />
        </div>
      )}
    </div>
  );
};

export default GeneralInfo;
